import { createModel } from "@rematch/core";
import { MessageCenterTypes } from "../types/message-center.types";
import { RootModel } from ".";
import { PaginationFactory } from "../factory";
import { RecipientCriteria } from "../services/compose-search.types";

export interface MessageCenterStoreProps {
    // Add edit mode state
    editMode: {
        isEditing: boolean;
        batchId: string | null;
        originalBatch: MessageCenterTypes.MessageBatch | null;
        reminderId: string | null;
        originalReminder: MessageCenterTypes.MessageReminder | null;
    };
    selectedUserIds: string[];
    recipientCriteria: RecipientCriteria;
    formState: MessageCenterTypes.FormState;
    automatedReminderState: MessageCenterTypes.AutomatedReminderState;
    pagination: PaginationFactory<MessageCenterTypes.MessageBatch>,
    loading: {
        createMessage: boolean;
        getScheduledMessages: boolean;
        updateMessage: boolean;
        fetchMany: boolean;
    };
}

export const messageCenterInitialState: MessageCenterStoreProps = {
    editMode: {
        isEditing: false,
        batchId: null,
        originalBatch: null,
        reminderId: null,
        originalReminder: null,
    },
    selectedUserIds: [],
    recipientCriteria: {
        groupIds: [],
        teamIds: [],
        userIds: [],
        selectAllUsers: false,
        activeFilter: 'all',
        tagFilters: [],
        tagLogic: 'AND',
    },
    formState: {
        title: "",
        body: "",
        channels: ['SMS'],
        messageType: "IMMEDIATE",
        scheduledDate: null,
        isAnnouncement: false,
        isCritical: false,
    },
    automatedReminderState: {
        reminderType: 'CHARGING',
        intervalHours: 24,
        daysOfWeek: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
        // Legacy system default (the old hardcoded cycle); the form swaps in
        // the viewer's detected timezone on mount via defaultTimezone().
        sendTimes: ['08:00'],
        timezone: 'America/New_York',
        timezoneStrategy: 'fixed',
        title: '',
        body: '',
        channels: ['SMS'],
    },
    loading: {
        createMessage: false,
        getScheduledMessages: false,
        updateMessage: false,
        fetchMany: false,
    },
    pagination: {} as PaginationFactory<MessageCenterTypes.MessageBatch>,
}

export const messageCenter = createModel<RootModel>()({
    state: messageCenterInitialState,
    reducers: {
        // Edit mode management
        setEditMode: (state, payload: { batchId: string | null, originalBatch: MessageCenterTypes.MessageBatch | null, reminderId: string | null, originalReminder: MessageCenterTypes.MessageReminder | null, }) => {
            state.editMode = {
                isEditing: true,
                batchId: payload.batchId,
                originalBatch: payload.originalBatch,
                reminderId: payload.reminderId,
                originalReminder: payload.originalReminder,
            };
        },
        exitEditMode: (state) => {
            state.editMode = messageCenterInitialState.editMode;
        },

        // selectedUserIds management
        updateSelectedUserIds: (state, payload: string[]) => {
            state.selectedUserIds = payload
        },
        resetSelectedUserIds: (state) => {
            state.selectedUserIds = messageCenterInitialState.selectedUserIds;
        },

        // RecipientCriteria management
        setRecipientCriteria: (state, payload: RecipientCriteria) => {
            state.recipientCriteria = payload;
        },
        updateRecipientCriteria: (state, payload: Partial<RecipientCriteria>) => {
            state.recipientCriteria = { ...state.recipientCriteria, ...payload };
        },
        resetRecipientCriteria: (state) => {
            state.recipientCriteria = messageCenterInitialState.recipientCriteria;
        },

        // Form state management
        updateFormState: (state, payload: Partial<MessageCenterTypes.FormState>) => {
            state.formState = { ...state.formState, ...payload };
        },
        resetFormState: (state) => {
            state.formState = messageCenterInitialState.formState;
        },

        // Automated reminder state management
        updateAutomatedReminderState: (state, payload: Partial<MessageCenterTypes.AutomatedReminderState>) => {
            state.automatedReminderState = { ...state.automatedReminderState, ...payload };
        },
        resetAutomatedReminderState: (state) => {
            state.automatedReminderState = messageCenterInitialState.automatedReminderState;
        },

        // Pagination
        setPagination: (store, pagination: PaginationFactory<MessageCenterTypes.MessageBatch>) => {
            store.pagination = pagination;
        },

        // Loading state management
        toggleLoading: (
            state,
            prop: keyof MessageCenterStoreProps['loading'],
            value: boolean
        ) => {
            state.loading[prop] = value;
        },

        // Complete reset (for when user finishes flow)
        resetAll: (state) => {
            return messageCenterInitialState;
        },
    }
});